import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiErrorMessage } from "@/lib/api/errors";
import { useOfflineCreate, type OfflineCreateVars } from "@/lib/api/offline-mutations";
import { openPdfBlob } from "@/lib/pdf-open";

import { consultationsApi } from "./api";
import type { Consultation, ConsultationFilters, ConsultationWritePayload } from "./api";

const KEY = "consultations";

/** Clé de mutation de création — partagée avec le registre offline. */
export const CONSULTATIONS_CREATE_KEY = [KEY, "create"] as const;

export function useConsultations(
  filters: ConsultationFilters = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => consultationsApi.list(filters),
    placeholderData: (prev) => prev,
    enabled: opts.enabled ?? true,
  });
}

export function useConsultation(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => consultationsApi.get(id!),
    enabled: !!id,
  });
}

/** Série d'évolution des constantes vitales d'un patient (courbe). */
export function usePatientVitalsSeries(patientId: string | undefined) {
  return useQuery({
    queryKey: [KEY, "vitals-series", patientId],
    queryFn: () => consultationsApi.vitalsSeries(patientId!),
    enabled: !!patientId,
  });
}

/** Catalogue des motifs de consultation (suggestions du champ motif). */
export function useConsultationReasons() {
  return useQuery({
    queryKey: [KEY, "reasons"],
    queryFn: () => consultationsApi.reasons(),
    staleTime: 10 * 60 * 1000,
  });
}

/** Recherche dans le catalogue CIM-10 (combobox diagnostic). */
export function useIcd10Search(search: string) {
  return useQuery({
    queryKey: [KEY, "icd10", search],
    queryFn: () => consultationsApi.searchIcd10(search),
    enabled: search.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateConsultation() {
  const qc = useQueryClient();
  const mutation = useMutation<Consultation, unknown, OfflineCreateVars<ConsultationWritePayload>>({
    mutationKey: [...CONSULTATIONS_CREATE_KEY],
    mutationFn: ({ payload, idempotencyKey }) => consultationsApi.create(payload, idempotencyKey),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      toast.success("Consultation créée.");
    },
  });
  return useOfflineCreate(mutation);
}

export function useUpdateConsultation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof consultationsApi.update>[1]) =>
      consultationsApi.update(id, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      await qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
      toast.success("Consultation mise à jour.");
    },
  });
}

export function useDeleteConsultation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => consultationsApi.remove(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      toast.success("Consultation supprimée.");
    },
  });
}

/** Ouvre le compte rendu PDF de la consultation. */
export function useConsultationReport() {
  return useMutation({
    mutationFn: ({ id }: { id: string; filename: string }) => consultationsApi.report(id),
    onSuccess: (blob, { filename }) => {
      if (openPdfBlob(blob, filename) === "downloaded") {
        toast.info("Fenêtre bloquée par le navigateur : le compte rendu a été téléchargé.");
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Impression du compte rendu impossible.")),
  });
}

/** Prépare (ou reprend) la facture brouillon d'une consultation (ADR 0047). */
export function useInvoiceConsultation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (acts: string[]) => consultationsApi.invoice(id, acts),
    onSuccess: async () => {
      // La consultation porte désormais sa facture ; la liste des factures aussi.
      await qc.invalidateQueries({ queryKey: [KEY] });
      await qc.invalidateQueries({ queryKey: ["sales"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Facturation impossible.")),
  });
}
